<template>
    <div
        v-if="isOpen"
        class="fixed left-14 w-[350px] bg-white top-0 z-[1001] h-full"
        style="box-shadow: 0px 0px 7px 4px rgba(0, 0, 0, 0.2)"
    >
        <div class="p-4 border-b flex justify-between items-center bg-gray-50">
            <h3 class="font-bold text-lg text-gray-800">Play Drawer</h3>
            <button
                @click="$emit('close')"
                class="text-gray-500 hover:text-red-500"
            >
                <ion-icon name="close-outline" class="text-2xl"></ion-icon>
            </button>
        </div>

        <div class="p-2 overflow-y-auto h-full">
            <ul class="space-y-1">
                <!-- Templates Folder -->
                <li>
                    <button
                        @click="isTemplatesOpen = !isTemplatesOpen"
                        class="
                            w-full
                            text-left
                            px-3
                            py-2
                            rounded-md
                            hover:bg-gray-100
                            flex
                            items-center
                            gap-2
                            transition-colors
                        "
                    >
                        <img
                            src="/default-images/icons/whistle-gray.svg"
                            width="30px"
                        />
                        <span class="font-medium text-sm text-gray-700 truncate"
                            >Templates</span
                        >
                        <ion-icon
                            :name="
                                isTemplatesOpen
                                    ? 'chevron-down'
                                    : 'chevron-forward'
                            "
                            class="ml-auto text-gray-400"
                        ></ion-icon>
                    </button>
                    <div
                        v-show="isTemplatesOpen"
                        class="
                            pl-6
                            pr-2
                            py-1
                            space-y-1
                            border-l-2 border-gray-100
                            ml-4
                            mb-2
                        "
                    >
                        <div
                            v-if="state.templateGroups.length === 0"
                            class="text-xs text-gray-400 py-2 italic"
                        >
                            No templates available
                        </div>
                        <ul v-else class="space-y-1 mt-2">
                            <li
                                v-for="group in state.templateGroups"
                                :key="group.playbook_id"
                            >
                                <button
                                    @click="
                                        activePlaybook =
                                            activePlaybook == group.playbook_id
                                                ? null
                                                : group.playbook_id
                                    "
                                    class="
                                        w-full
                                        text-left
                                        px-3
                                        py-1.5
                                        rounded-md
                                        hover:bg-gray-100
                                        flex
                                        items-center
                                        gap-2
                                        transition-colors
                                    "
                                >
                                    <img
                                        src="/default-images/icons/whistle-gray.svg"
                                        width="30px"
                                    />
                                    <span
                                        class="
                                            font-medium
                                            text-sm text-gray-700
                                            truncate
                                        "
                                        >{{ group.playbook_title }}</span
                                    >
                                    <ion-icon
                                        :name="
                                            activePlaybook === group.playbook_id
                                                ? 'chevron-down'
                                                : 'chevron-forward'
                                        "
                                        class="ml-auto text-gray-400 text-xs"
                                    ></ion-icon>
                                </button>
                                <div
                                    v-show="activePlaybook == group.playbook_id"
                                    class="
                                        pl-6
                                        pr-2
                                        py-1
                                        space-y-1
                                        border-l-2 border-gray-100
                                        ml-4
                                        mb-2
                                    "
                                >
                                    <div
                                        v-if="
                                            !group.plays ||
                                            group.plays.length === 0
                                        "
                                        class="
                                            text-xs text-gray-400
                                            py-2
                                            italic
                                        "
                                    >
                                        Empty folder
                                    </div>
                                    <button
                                        v-for="play in group.plays"
                                        :key="play.play_id"
                                        @click="$emit('loadTemplate', play)"
                                        class="
                                            w-full
                                            text-left
                                            px-2
                                            py-1.5
                                            rounded
                                            hover:bg-primary/10
                                            hover:text-primary
                                            flex
                                            items-center
                                            gap-3
                                            text-sm
                                            transition-colors
                                            group
                                            relative
                                            text-gray-600
                                        "
                                    >
                                        <div
                                            class="
                                                relative
                                                w-14
                                                h-10
                                                bg-gray-50
                                                border border-gray-200
                                                rounded
                                                flex-shrink-0
                                                overflow-hidden
                                                group-hover:border-primary/50
                                                transition-colors
                                            "
                                        >
                                            <img
                                                :src="play.thumbnail_url"
                                                class="
                                                    w-full
                                                    h-full
                                                    object-contain
                                                    mix-blend-multiply
                                                "
                                                onerror="this.onerror=null;this.src='/default-images/dummy-play.png';this.style.backgroundImage = 'none';"
                                            />
                                        </div>
                                        <span class="truncate pr-4 flex-grow">{{
                                            play.play_name
                                        }}</span>
                                    </button>
                                </div>
                            </li>
                        </ul>
                    </div>
                </li>

                <!-- Playbook Folders -->
                <li
                    v-for="playbook in state.playbooks"
                    :key="playbook.playbook_id"
                >
                    <button
                        @click="
                            activePlaybook =
                                activePlaybook == playbook.playbook_id
                                    ? null
                                    : playbook.playbook_id
                        "
                        class="
                            w-full
                            text-left
                            px-3
                            py-2
                            rounded-md
                            hover:bg-gray-100
                            flex
                            items-center
                            gap-2
                            transition-colors
                        "
                    >
                        <img
                            src="/default-images/icons/whistle-gray.svg"
                            width="30px"
                        />
                        <span
                            class="font-medium text-sm text-gray-700 truncate"
                            >{{ playbook.playbook_title }}</span
                        >
                        <ion-icon
                            :name="
                                activePlaybook == playbook.playbook_id
                                    ? 'chevron-down'
                                    : 'chevron-forward'
                            "
                            class="ml-auto text-gray-400"
                        ></ion-icon>
                    </button>
                    <div
                        v-show="activePlaybook == playbook.playbook_id"
                        class="
                            pl-6
                            pr-2
                            py-1
                            space-y-1
                            border-l-2 border-gray-100
                            ml-4
                            mb-2
                        "
                    >
                        <div
                            v-if="playbook.length === 0"
                            class="text-xs text-gray-400 py-2 italic"
                        >
                            Empty folder
                        </div>
                        <button
                            v-for="play in playbook.plays"
                            :key="play.play_id"
                            @click="$emit('loadPlay', play)"
                            class="
                                w-full
                                text-left
                                px-2
                                py-1.5
                                rounded
                                hover:bg-primary/10 hover:text-primary
                                flex
                                items-center
                                gap-3
                                text-sm
                                transition-colors
                                group
                                relative
                            "
                            :class="
                                activePlay && activePlay.play_id == play.play_id
                                    ? 'bg-primary/5 text-primary border border-primary/20'
                                    : 'text-gray-600 border border-transparent'
                            "
                        >
                            <div
                                class="
                                    relative
                                    w-14
                                    h-10
                                    bg-gray-50
                                    border
                                    rounded
                                    flex-shrink-0
                                    overflow-hidden
                                    group-hover:border-primary/50
                                    transition-colors
                                "
                                :class="
                                    activePlay &&
                                    activePlay.play_id == play.play_id
                                        ? 'border-primary'
                                        : 'border-gray-200'
                                "
                            >
                                <img
                                    :src="
                                        play.thumbnail_url
                                            ? play.thumbnail_url
                                            : '/default-images/dummy-play.png'
                                    "
                                    class="
                                        w-full
                                        h-full
                                        object-contain
                                        mix-blend-multiply
                                    "
                                    onerror="this.onerror=null;this.src='/default-images/dummy-play.png';this.style.backgroundImage = 'none';"
                                />
                            </div>
                            <span class="truncate pr-4 flex-grow">{{
                                play.play_name
                            }}</span>

                            <!-- Current Play Indicator -->
                            <div
                                v-if="
                                    activePlay &&
                                    activePlay.play_id === play.play_id
                                "
                                class="
                                    absolute
                                    right-2
                                    top-1/2
                                    -translate-y-1/2
                                    flex
                                    h-2
                                    w-2
                                "
                            >
                                <span
                                    class="
                                        animate-ping
                                        absolute
                                        inline-flex
                                        h-full
                                        w-full
                                        rounded-full
                                        bg-green-400
                                        opacity-75
                                    "
                                ></span>
                                <span
                                    class="
                                        relative
                                        inline-flex
                                        rounded-full
                                        h-2
                                        w-2
                                        bg-green-500
                                    "
                                ></span>
                            </div>
                        </button>
                    </div>
                </li>
            </ul>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
    activePlay: {
        type: Object,
        default: null,
    },
    teamId: {
        type: String,
        default: null,
    },
});

const state = reactive({
    templateGroups: [],
    playbooks: [],
});

const isTemplatesOpen = ref(false);
const activePlaybook = ref(null);

if (props.activePlay) {
    if (props.activePlay.is_template) {
        isTemplatesOpen.value = true;
    }
    activePlaybook.value = props.activePlay.playbook_id;
}

const emit = defineEmits(["close", "loadPlay", "loadTemplate"]);

async function loadPlaybooks(play) {
    const dataPlaybooks = await axios.get("/playbook/list", {
        params: {
            teamId: props.teamId,
        },
    });
    const dataTemplates = await axios.get("/playbook/list", {
        params: {
            teamId: props.teamId,
            isTemplate: 1,
        },
    });
    state.templateGroups = dataTemplates.data;
    state.playbooks = dataPlaybooks.data;
}

onMounted(() => {
    loadPlaybooks();
});
</script>
